import { useEffect, useState } from 'react';
import { useUIStore } from '../store/uiStore';
import { useAuthStore } from '../store/authStore';
import apiClient from '../services/apiClient';
import { X, ExternalLink, Building2, Loader2, AlertCircle } from 'lucide-react';

/** Hosts de desarrollo local: solo entre ellos se alinea el hostname. */
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '::1'];

/**
 * Base del iframe = el portal Laravel (INTUS_API_URL), NO el host de la SPA.
 * `/patient/sso/{token}` vive en app.vita360.co; apuntarlo a
 * pacientes.vita360.co rompía el modal (el SPA no tiene esa ruta). El
 * hostname solo se alinea cuando AMBOS son loopback (dev: la app abierta
 * como 127.0.0.1 y el backend en localhost), que era el motivo original.
 */
export function resolveConsultorioBase(rawBaseUrl: string | null | undefined): string {
  if (!rawBaseUrl) {
    return '';
  }

  try {
    const parsed = new URL(rawBaseUrl);
    const alignLoopback =
      LOOPBACK_HOSTS.includes(parsed.hostname) && LOOPBACK_HOSTS.includes(window.location.hostname);

    const hostname = alignLoopback ? window.location.hostname : parsed.hostname;

    return `${parsed.protocol}//${hostname}${parsed.port ? `:${parsed.port}` : ''}`;
  } catch {
    return '';
  }
}

/**
 * Modal "Consultorio": muestra el dashboard del paciente de Vita360
 * (Laravel) embebido en un iframe casi a pantalla completa, SIN salir de
 * la app. Usa el patrón currentModal/openModal/closeModal del uiStore.
 *
 * Flujo: al abrir el modal, la app pide al backend Node un token SSO fresco
 * (POST /auth/consultorio-sso, usa el accessToken de la sesión) y el iframe
 * apunta a {base}/patient/sso/{token}. Laravel valida el token, autentica
 * al paciente en el guard 'patient' y redirige a su dashboard. Así el
 * iframe muestra el dashboard logueado, no el portal de ingreso.
 */
export default function ConsultorioModal() {
  const { currentModal, closeModal } = useUIStore();
  const { intusBaseUrl } = useAuthStore();

  const isOpen = currentModal === 'consultorio';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string>('');

  useEffect(() => {
    if (!isOpen) {
      setIframeUrl('');
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient
      .post<{ ssoToken: string; baseUrl: string }>('/auth/consultorio-sso')
      .then(({ data }) => {
        if (cancelled) {
          return;
        }

        const base = resolveConsultorioBase(data.baseUrl || intusBaseUrl);

        if (!base) {
          setError('No se pudo conectar con el consultorio.');
          return;
        }

        setIframeUrl(`${base}/patient/sso/${encodeURIComponent(data.ssoToken)}`);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setError(err?.response?.data?.error || 'No se pudo conectar con el consultorio.');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const consultorioUrl = iframeUrl;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-background"
      role="dialog"
      aria-modal="true"
      aria-label="Mi consultorio en Vita360"
    >
      {/* Barra superior del modal */}
      <div className="flex items-center justify-between border-b bg-card px-4 py-3 md:px-6">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-sm font-semibold leading-tight md:text-base">Mi consultorio en Vita360</h2>
            <p className="text-xs text-v-on-surface-variant">Citas, documentos e historia clínica</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {consultorioUrl && (
            <a
              href={consultorioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-v-on-surface-variant transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">Abrir en pestaña</span>
            </a>
          )}
          <button
            type="button"
            onClick={closeModal}
            aria-label="Cerrar consultorio"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-v-on-surface-variant transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Iframe del dashboard del paciente de Vita360 */}
      <div className="relative flex-1 bg-secondary/30">
        {loading && !iframeUrl && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary/60" />
            <p className="text-sm text-v-on-surface-variant">Abriendo tu consultorio…</p>
          </div>
        )}

        {error && !iframeUrl && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive/60" />
            <p className="max-w-sm text-sm text-v-on-surface-variant">{error}</p>
            <a
              href={intusBaseUrl ? `${intusBaseUrl.replace(/\/+$/, '')}/patient/login` : undefined}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:brightness-105"
            >
              Ir al portal de Vita360
            </a>
          </div>
        )}

        {iframeUrl && (
          <iframe
            key={iframeUrl}
            src={iframeUrl}
            title="Consultorio Vita360"
            className="h-full w-full border-0"
            allow="camera; microphone; clipboard-write"
          />
        )}
      </div>
    </div>
  );
}

